import React, { useRef, useState } from "react";
import styled from "styled-components";
import { OpenAndCloseEye } from "./CommonsSalta";
import { estilosProvNew } from "../../configProvinciasNew";

const admittedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Tab"];
const separator = "-";

const InputSalta = ({
  placeholder,
  id,
  label,
  value,
  setValue,
  type,
  required,
  withDecimals,
  helperText,
  helperTextAction,
  isHelperTextLink,
  errorMessage,
  isCuil,
  isTicket,
  handleCheckErrors,
  extraInfo,
}) => {
  const estiloProv = estilosProvNew[process.env.REACT_APP_COD_ORGANIZACION]

  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);

  const inputType =
    type === "password" ? (showPassword ? "text" : "password") : type;

  const handleKeyDown = (event) => {
    const { key } = event;

    if (type === "number") {
      if (withDecimals) {
        // Allow numbers, one ".", and prevent more than two decimals
        if (
          !/^\d$/.test(key) &&
          !admittedKeys.includes(key) &&
          (key !== "." || value.includes("."))
        ) {
          event.preventDefault();
        }

        // Ensure only two decimal places are allowed
        if (
          value?.includes(".") &&
          value?.split(".")[1].length >= 2 &&
          /^\d$/.test(key)
        ) {
          event.preventDefault();
        }
      } else if (!/^\d$/.test(key) && !admittedKeys.includes(key)) {
        // Only allow numbers
        event.preventDefault();
      }
    }
  };

  const formatCuil = (input) => {
    const onlyNumbers = input.replace(/\D/g, "").slice(0, 11); // Max 11 digits
    if (onlyNumbers.length <= 2) {
      return onlyNumbers;
    } else if (onlyNumbers.length <= 10) {
      return `${onlyNumbers.slice(0, 2)}${separator}${onlyNumbers.slice(2)}`;
    } else {
      return `${onlyNumbers.slice(0, 2)}${separator}${onlyNumbers.slice(
        2,
        10
      )}${separator}${onlyNumbers.slice(10)}`;
    }
  };

  const formatTicket = (input) => {
    const onlyNumbers = input.replace(/\D/g, "").slice(0, 13); // Max 13 digits

    if (onlyNumbers.length <= 5) {
      return onlyNumbers;
    } else if (onlyNumbers.length <= 7) {
      return `${onlyNumbers.slice(0, 5)}${separator}${onlyNumbers.slice(5)}`;
    } else {
      return `${onlyNumbers.slice(0, 5)}${separator}${onlyNumbers.slice(
        5,
        7
      )}${separator}${onlyNumbers.slice(7)}`;
    }
  };

  const handleChange = (value) => {
    if (isCuil || isTicket) {
      let formattedValue = "";
      isCuil && (formattedValue = formatCuil(value));
      isTicket && (formattedValue = formatTicket(value));

      handleCheckErrors(id, formattedValue);
      setValue(formattedValue);

      // to adjust a correct cursor position
      const inputElement = inputRef.current;
      const cursorPositionBeforeFormat = inputElement
        ? inputElement.selectionStart
        : 0;
      requestAnimationFrame(() => {
        if (inputElement) {
          let newCursorPos = cursorPositionBeforeFormat;
          let separatorsBefore = 0;
          let separatorsAfter = 0;

          // Count separators before the original cursor position in the raw value
          for (let i = 0; i < cursorPositionBeforeFormat; i++) {
            value[i] === separator && separatorsBefore++;
          }

          // Count separators before the calculated new cursor position in the formatted value
          for (let i = 0; i < newCursorPos; i++) {
            formattedValue[i] === separator && separatorsAfter++;
          }

          // Adjust the cursor position by the difference in the number of separators
          newCursorPos += separatorsAfter - separatorsBefore;

          newCursorPos = Math.min(newCursorPos, formattedValue.length);
          inputElement.setSelectionRange(newCursorPos, newCursorPos);
        }
      });
      return;
    }
    handleCheckErrors(id, value);
    setValue(value);
  };

  return (
    <div style={{ margin: "12px 0" }}>
      <div style={{ position: "relative" }}>
        <InputCustom
          ref={inputRef}
          type={inputType}
          id={id}
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          handleKeyDown={handleKeyDown}
          autoComplete="new-password"
        />
        {type === "password" && (
          <EyeIcon onClick={() => setShowPassword((prev) => !prev)}>
            <OpenAndCloseEye
              passVisible={showPassword}
              styles={{ position: "absolute", top: -13, right: -5 }}
            />
          </EyeIcon>
        )}
        <LabelCustom bgLabel={estiloProv.backgroundLight} for={id}>
          {label} {required && " *"} {extraInfo}
        </LabelCustom>
      </div>
      {helperText && (
        <HelperTextCustom
          onClick={() => helperTextAction?.()}
          hasAction={!!helperTextAction}
          isHelperTextLink={isHelperTextLink}
        >
          {helperText}
        </HelperTextCustom>
      )}
      {errorMessage && <ErrorMsgCustom>{errorMessage}</ErrorMsgCustom>}
    </div>
  );
};

export default InputSalta;

const LabelCustom = styled.label`
  bottom: 22px;
  left: 10px;
  position: absolute;
  background-color: ${(props) => props.bgLabel};
  padding: 0px 5px 0px 5px;
  font-size: 0.9rem;
  transition: 0.2s;
  pointer-events: none;
  color: #49454f;
`;

const EyeIcon = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 18px;
  user-select: none;
`;

const InputCustom = styled.input`
  display: block;
  width: 100%;
  border-radius: 4px;
  border: 1px solid #79747e;
  padding: 8px 26px 8px 14px;
  &::placeholder {
    color: #d0d5da;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const HelperTextCustom = styled.p`
  color: #49454f;
  font-size: 0.8rem;
  margin: 0;
  padding: 0 14px;
  cursor: ${(props) => (props.hasAction ? "pointer" : "default")};
  width: ${(props) => (props.hasAction ? "fit-content" : "auto")};
  transition: all 0.3s ease;

  &:hover {
    color: ${(props) => (props.isHelperTextLink ? "#2886eb" : "#49454f")};
  }
`;

const ErrorMsgCustom = styled.p`
  color: rgb(218, 3, 3);
  font-size: 0.8rem;
  margin: 5px 0;
  padding: 0 14px;
`;

export const InputSelectSalta = ({
  id,
  label,
  value,
  setValue,
  options,
  required,
  helperText,
  errorMessage,
  handleCheckErrors,
}) => {
  const estiloProv = estilosProvNew[process.env.REACT_APP_COD_ORGANIZACION]
  return (
    <div style={{ margin: "12px 0" }}>
      <div style={{ position: "relative" }}>
        <SelectCustom
          id={id}
          value={value}
          onChange={(e) => {
            handleCheckErrors(id, e.target.value);
            setValue(e.target.value);
          }}
          required={required}
        >
          <option disabled selected value={undefined}>
            Seleccione una opción
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectCustom>
        <SelectIcon>
          <span
            data-width="15px"
            className="iconify op-text"
            data-icon="bxs:down-arrow"
            style={{ color: "#9b44ff" }}
            data-inline="false"
          ></span>
        </SelectIcon>
        <LabelCustom bgLabel={estiloProv.backgroundLight} htmlFor={id}>
          {label} {required && " *"}
        </LabelCustom>
      </div>
      {helperText && <HelperTextCustom>{helperText}</HelperTextCustom>}
      {errorMessage && <ErrorMsgCustom>{errorMessage}</ErrorMsgCustom>}
    </div>
  );
};

const SelectCustom = styled.select`
  display: block;
  width: 100%;
  border-radius: 4px;
  border: 1px solid #79747e;
  padding: 8px 14px;
  background: white;
  appearance: none;
  cursor: pointer;
`;

const SelectIcon = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;
